import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { MessageCircleMore, PanelsTopLeft } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import type { SeoDocument } from "../seoConfig";
import { downloadPath, leadFormPath } from "../routes";
import { reportLeadEntryConversion } from "../tracking";
import { usePageActivity } from "../hooks/usePageActivity";
import { useDentalSectionReveal } from "../hooks/useDentalSectionReveal";
import { installDentalSectionNavigation } from "../dentalSectionNavigation";
import { installDentalScrollRestoration } from "../dentalReloadScroll";
import { installDentalSectionAnalytics } from "../dentalSectionAnalytics";
import { Nav } from "./Nav";
import { Seo } from "./Seo";
import { DentalHeroExecution } from "./dental/DentalHeroExecution";
import { DentalFrontDeskStory } from "./dental/DentalFrontDeskStory";
import { DentalImpactGrowth } from "./dental/DentalImpactGrowth";
import { DentalConversationDashboard } from "./dental/DentalConversationDashboard";
import { DentalSystemIntegrations } from "./dental/DentalSystemIntegrations";
import { DentalPmsIntegrationVideo } from "./dental/DentalPmsIntegrationVideo";
import { DentalFaq } from "./dental/DentalFaq";
import { DentalPageNavigation } from "./dental/DentalPageNavigation";
import { Pricing } from "./Pricing";
import { DentalSchedulingBrowser } from "./dental/DentalSchedulingBrowser";

type DentalScenario = {
  id: string;
  tab: string;
  transcript: Array<{
    speaker: "Patient" | "ClawEase";
    text: string;
    summaryCount: number;
  }>;
  collected: Array<{
    label: string;
    value: string;
  }>;
  outcome: {
    title: string;
    description: string;
  };
  steps: Array<{
    label: string;
    title: string;
    body: string;
    rows: Array<{ label: string; value: string; tone?: "routine" | "schedule" | "handoff" }>;
  }>;
};

const dentalScenarios: DentalScenario[] = [
  {
    id: "appointment-request",
    tab: "Book an appointment",
    transcript: [
      { speaker: "ClawEase", text: "Welcome! I can help you book a dental appointment. What day and time would you prefer?", summaryCount: 0 },
      { speaker: "Patient", text: "I'm a new patient. Is next Tuesday at 4:30 PM available?", summaryCount: 1 },
      { speaker: "ClawEase", text: "Of course. What would you like to come in for?", summaryCount: 1 },
      { speaker: "Patient", text: "I'd like a routine dental cleaning.", summaryCount: 2 },
      { speaker: "ClawEase", text: "Thanks — I've selected Cleaning. I'll check the clinic's available appointment times.", summaryCount: 2 },
      { speaker: "ClawEase", text: "Next Tuesday at 4:30 PM isn't available. Would you like me to find another time?", summaryCount: 2 },
      { speaker: "Patient", text: "Yes. Is there anything on a weekday after 4:30 PM?", summaryCount: 3 },
      { speaker: "ClawEase", text: "I'll check the next available weekday openings.", summaryCount: 3 },
      { speaker: "ClawEase", text: "I found Dr. Maya Chen available this Thursday at 4:45 PM for your cleaning. Would you like me to use that time?", summaryCount: 4 },
      { speaker: "Patient", text: "Yes, Thursday at 4:45 PM works for me.", summaryCount: 4 },
      { speaker: "ClawEase", text: "Great. To create your patient record, may I have your full name and mobile number?", summaryCount: 4 },
      { speaker: "Patient", text: "Jordan Lee, +1 202-555-0147.", summaryCount: 6 },
      { speaker: "ClawEase", text: "Your routine dental cleaning has been submitted for Thursday from 4:45 PM to 5:45 PM. Is there anything else I can help you with?", summaryCount: 6 },
      { speaker: "Patient", text: "No, that's all. Thank you very much.", summaryCount: 6 },
      { speaker: "ClawEase", text: "You're very welcome, Jordan. Have a wonderful day!", summaryCount: 6 },
    ],
    collected: [
      { label: "Service", value: "Routine dental cleaning" },
      { label: "Initial request", value: "Tuesday · 4:30 PM unavailable" },
      { label: "Preferred window", value: "Weekdays after 4:30 PM" },
      { label: "Matched opening", value: "Thursday · 4:45–5:45 PM" },
      { label: "Patient name", value: "Jordan Lee" },
      { label: "Mobile", value: "+1 202-555-0147" },
    ],
    outcome: {
      title: "Appointment submitted",
      description: "The fictional visit is created after the patient record and appointment details are confirmed.",
    },
    steps: [
      {
        label: "Request",
        title: "Identify a routine cleaning request.",
        body: "The workflow identifies a routine cleaning request and keeps clinical questions out of the automated path.",
        rows: [
          { label: "Request type", value: "New-patient cleaning appointment", tone: "routine" },
          { label: "Clinical advice", value: "Not requested", tone: "routine" },
          { label: "Next permitted step", value: "Collect approved fields", tone: "schedule" },
        ],
      },
      {
        label: "Details",
        title: "Capture the details your front desk needs.",
        body: "The sample groups contact, visit-context and preference fields into one reviewable request.",
        rows: [
          { label: "Patient type", value: "New patient", tone: "routine" },
          { label: "Visit reason", value: "Routine dental cleaning", tone: "routine" },
          { label: "Preferred location", value: "North clinic", tone: "schedule" },
        ],
      },
      {
        label: "Next step",
        title: "Prepare an approved scheduling next step.",
        body: "Fictional availability illustrates what a controlled workflow could show before a supported booking action is enabled.",
        rows: [
          { label: "Preference", value: "Weekday after 4:30 PM", tone: "schedule" },
          { label: "Sample option", value: "Thursday · 4:45–5:45 PM", tone: "schedule" },
          { label: "Fallback", value: "Front-desk follow-up", tone: "handoff" },
        ],
      },
      {
        label: "Owner & status",
        title: "Give the request a clear owner and status.",
        body: "The request leaves the conversation as a consistent summary instead of another voicemail to interpret.",
        rows: [
          { label: "Record", value: "Review-ready request", tone: "routine" },
          { label: "Next owner", value: "Front-desk team", tone: "handoff" },
          { label: "Customer message", value: "Appointment submitted", tone: "schedule" },
        ],
      },
    ],
  },
  {
    id: "reschedule",
    tab: "Reschedule",
    transcript: [
      { speaker: "Patient", text: "I need to move tomorrow's cleaning appointment.", summaryCount: 2 },
      { speaker: "ClawEase", text: "Of course. I can prepare a reschedule request without losing the existing details.", summaryCount: 2 },
      { speaker: "Patient", text: "Any afternoon later this week would be better.", summaryCount: 3 },
      { speaker: "ClawEase", text: "Thursday afternoon is available as a sample option. Should I prepare that?", summaryCount: 4 },
      { speaker: "Patient", text: "Yes, please.", summaryCount: 4 },
      { speaker: "ClawEase", text: "Done. The front desk has the original visit and proposed change together.", summaryCount: 4 },
    ],
    collected: [
      { label: "Request", value: "Reschedule" },
      { label: "Existing visit", value: "Cleaning · tomorrow" },
      { label: "Preferred window", value: "Afternoon later this week" },
      { label: "Proposed time", value: "Thursday afternoon" },
    ],
    outcome: {
      title: "Ready to confirm the new time",
      description: "The original visit and proposed change stay together.",
    },
    steps: [
      {
        label: "Request",
        title: "Recognize an appointment-change request.",
        body: "The workflow keeps the current visit context visible before any approved change step is offered.",
        rows: [
          { label: "Request type", value: "Reschedule", tone: "schedule" },
          { label: "Existing context", value: "Preserved", tone: "routine" },
          { label: "Next permitted step", value: "Confirm preferences", tone: "schedule" },
        ],
      },
      {
        label: "Details",
        title: "Keep the existing appointment context.",
        body: "Only the change reason and approved scheduling preferences need to be added to the existing context.",
        rows: [
          { label: "Current visit", value: "Reference retained", tone: "routine" },
          { label: "Unavailable window", value: "Current time", tone: "schedule" },
          { label: "Preferred window", value: "Afternoon", tone: "schedule" },
        ],
      },
      {
        label: "Next step",
        title: "Prepare an approved option or a staff task.",
        body: "If a supported booking action is not enabled, the same context becomes a complete staff request.",
        rows: [
          { label: "Sample option", value: "Thursday afternoon", tone: "schedule" },
          { label: "Confirmation", value: "Required before change", tone: "handoff" },
          { label: "Fallback", value: "Send request to staff", tone: "handoff" },
        ],
      },
      {
        label: "Owner & status",
        title: "Keep the old and proposed context together.",
        body: "The front desk sees what should change, what remains the same and whether the patient is awaiting confirmation.",
        rows: [
          { label: "Original context", value: "Retained", tone: "routine" },
          { label: "Proposed change", value: "Visible", tone: "schedule" },
          { label: "Status", value: "Awaiting clinic confirmation", tone: "handoff" },
        ],
      },
    ],
  },
  {
    id: "urgent",
    tab: "Clinical / urgent",
    transcript: [
      { speaker: "Patient", text: "I have swelling and I'm not sure what I should do.", summaryCount: 2 },
      { speaker: "ClawEase", text: "I'm sorry you're dealing with that. I can't assess symptoms or recommend treatment.", summaryCount: 3 },
      { speaker: "ClawEase", text: "I'll stop the routine booking path and follow the clinic's approved handoff.", summaryCount: 4 },
      { speaker: "Patient", text: "Please have the clinic contact me.", summaryCount: 4 },
      { speaker: "ClawEase", text: "The permitted context is ready for clinic staff to review.", summaryCount: 4 },
    ],
    collected: [
      { label: "Request type", value: "Clinical question" },
      { label: "Reported concern", value: "Swelling" },
      { label: "Automated advice", value: "Not provided" },
      { label: "Next owner", value: "Clinic staff" },
    ],
    outcome: {
      title: "Human handoff prepared",
      description: "Only permitted context moves to the clinic's approved path.",
    },
    steps: [
      {
        label: "Request",
        title: "Do not convert a clinical concern into a routine booking flow.",
        body: "The workflow does not diagnose, assess urgency or recommend treatment.",
        rows: [
          { label: "Signal", value: "Clinical question", tone: "handoff" },
          { label: "Automated advice", value: "Not permitted", tone: "handoff" },
          { label: "Next step", value: "Use approved escalation", tone: "handoff" },
        ],
      },
      {
        label: "Details",
        title: "Collect only the context the handoff rule permits.",
        body: "The production rule, destination and approved message must be verified by the clinic before launch.",
        rows: [
          { label: "Contact context", value: "Included if approved", tone: "routine" },
          { label: "Clinical interpretation", value: "Not performed", tone: "handoff" },
          { label: "Routing rule", value: "Clinic-defined", tone: "handoff" },
        ],
      },
      {
        label: "Next step",
        title: "The routine booking path stops here.",
        body: "Clinical or urgent language exits the routine workflow instead of improvising an answer.",
        rows: [
          { label: "Routine availability", value: "Not shown", tone: "handoff" },
          { label: "Care recommendation", value: "Not provided", tone: "handoff" },
          { label: "Approved message", value: "Clinic-configured", tone: "schedule" },
        ],
      },
      {
        label: "Owner & status",
        title: "Automation stops. Qualified staff take over.",
        body: "The handoff carries permitted context, while care decisions remain entirely with the clinic and its approved emergency instructions.",
        rows: [
          { label: "Automated path", value: "Stopped", tone: "handoff" },
          { label: "Next owner", value: "Clinic staff", tone: "handoff" },
          { label: "Launch requirement", value: "Escalation tested", tone: "schedule" },
        ],
      },
    ],
  },
];

function DentalWorkflowConsole() {
  const consoleRef = useRef<HTMLDivElement>(null);
  const conversationViewportRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(consoleRef, { amount: 0.45 });
  const prefersReducedMotion = useReducedMotion();
  const pageActive = usePageActivity();
  const [visibleMessageCount, setVisibleMessageCount] = useState(0);
  const [focusWithin, setFocusWithin] = useState(false);
  const scenario = dentalScenarios[0];

  useEffect(() => {
    setVisibleMessageCount(prefersReducedMotion ? scenario.transcript.length : 0);
  }, [prefersReducedMotion, scenario.transcript.length]);

  const canAutoPlay = isInView && pageActive && !focusWithin && !prefersReducedMotion;

  useEffect(() => {
    if (!canAutoPlay) return;
    const atEnd = visibleMessageCount >= scenario.transcript.length;
    const holdByMessageCount: Record<number, number> = {
      0: 480,
      1: 1550,
      2: 1700,
      3: 1800,
      4: 1900,
      5: 1700,
      6: 1900,
      7: 1600,
      8: 2200,
      9: 2800,
      10: 1600,
      11: 1900,
      12: 6600,
      13: 2500,
      14: 1900,
    };
    const delay = atEnd ? 4600 : holdByMessageCount[visibleMessageCount] ?? 1600;
    const timerId = window.setTimeout(() => {
      if (atEnd) {
        setVisibleMessageCount(0);
        return;
      }
      setVisibleMessageCount((current) => current + 1);
    }, delay);
    return () => window.clearTimeout(timerId);
  }, [canAutoPlay, scenario.transcript.length, visibleMessageCount]);

  useEffect(() => {
    const viewport = conversationViewportRef.current;
    if (!viewport) return;

    if (visibleMessageCount === 0) {
      viewport.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (viewport.scrollHeight <= viewport.clientHeight) return;
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [prefersReducedMotion, visibleMessageCount]);

  return (
    <div
      ref={consoleRef}
      data-visible-message-count={visibleMessageCount}
      onFocusCapture={() => setFocusWithin(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusWithin(false);
      }}
      className="mt-10 w-full lg:mt-6"
    >
      <div className="overflow-hidden rounded-[28px] border border-[#e3d9ea] bg-white shadow-[0_28px_76px_rgba(45,20,68,.10)]">
        <div className="grid lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
          <section aria-label="WebChat" className="relative flex min-w-0 flex-col overflow-hidden border-b border-[#e8dfea] bg-[linear-gradient(145deg,#f3eef6_0%,#f8f4fa_70%,#f1eaf5_100%)] lg:border-b-0 lg:border-r">
            <header className="flex h-10 shrink-0 items-center gap-2 border-b border-[#eee7f2] bg-white px-5 sm:px-6">
              <MessageCircleMore aria-hidden="true" className="h-4 w-4 shrink-0 text-[#6d35cf]" />
              <h3 className="text-[14px] font-bold tracking-[-.02em] text-[#6d35cf]">WebChat</h3>
            </header>
            <div className="relative h-[410px] lg:h-auto lg:min-h-0 lg:flex-1">
              <motion.div
                ref={conversationViewportRef}
                className="absolute inset-0 overflow-y-hidden px-5 sm:px-6"
                style={{ overflowAnchor: "none" }}
              >
                <div aria-hidden="true" className="flex min-h-full flex-col gap-3.5 py-3">
                  <AnimatePresence initial={false}>
                    {scenario.transcript.slice(0, visibleMessageCount).map((message, index) => {
                      const isClawEase = message.speaker === "ClawEase";
                      return (
                        <motion.div
                          key={`${scenario.id}-${index}-${message.text}`}
                          initial={{ opacity: 0, y: 14, scale: .985 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ duration: .32, ease: [0.22, 1, 0.36, 1] }}
                          className={`flex ${isClawEase ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`${isClawEase ? "max-w-[84%] text-right" : "max-w-[80%] text-left"}`}>
                            <div className={`mb-1 text-[8px] font-extrabold uppercase tracking-[.12em] ${isClawEase ? "text-[#6d35cf]" : "text-[#8c7f90]"}`}>{message.speaker}</div>
                            <div className={`rounded-[13px] px-3 py-2.5 text-left text-[12.5px] font-semibold leading-5 ${isClawEase ? "rounded-tr-[4px] bg-[linear-gradient(135deg,#51217e_0%,#702da2_100%)] text-white shadow-[0_8px_20px_rgba(76,33,117,.13)]" : "rounded-tl-[4px] bg-white text-[#3b313f] shadow-[0_7px_18px_rgba(55,35,64,.05)]"}`}>
                              {message.text}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                  {visibleMessageCount === 0 ? (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: .55 }} className="py-28 text-center text-[12px] font-semibold text-[#8f8293]">Conversation restarting…</motion.p>
                  ) : null}
                </div>
              </motion.div>

              <div className="sr-only">
                {scenario.transcript.map((message) => <p key={`${message.speaker}-${message.text}`}>{message.speaker}: {message.text}</p>)}
              </div>
            </div>
          </section>

          <section aria-label="Example Dental PMS" className="min-w-0 bg-[#e9edf3]">
            <header className="flex h-10 items-center gap-2 border-b border-[#eee7f2] bg-white px-5 sm:px-6">
              <PanelsTopLeft aria-hidden="true" className="h-4 w-4 shrink-0 text-[#6d35cf]" />
              <h3 className="text-[14px] font-bold tracking-[-.02em] text-[#172033]">Example Dental PMS</h3>
            </header>
            <div className="p-3 sm:p-4 lg:p-5">
              <DentalSchedulingBrowser
                visibleMessageCount={visibleMessageCount}
                transcriptLength={scenario.transcript.length}
                allowAutoPresentation={canAutoPlay}
                pageActive={pageActive}
                onInteract={() => setFocusWithin(true)}
              />
            </div>
          </section>
        </div>
      </div>

    </div>
  );
}

export function DentalClinicsPage({ seo }: { heroImage: string; seo: SeoDocument }) {
  const mainRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (mainRef.current) return installDentalScrollRestoration(mainRef.current);
  }, []);
  useDentalSectionReveal(mainRef);
  useEffect(() => {
    if (mainRef.current) return installDentalSectionNavigation(mainRef.current);
  }, []);
  useEffect(() => {
    if (mainRef.current) return installDentalSectionAnalytics(mainRef.current);
  }, []);
  const location = useLocation();
  const pageActive = usePageActivity();
  const contactPath = leadFormPath("en", undefined, "industry_dental-clinics_hero", location.search);
  const heroDownloadPath = downloadPath("en", "industry_dental-clinics_hero", undefined, location.search, location.pathname);

  useEffect(() => {
    document.documentElement.dataset.animationState = pageActive ? "running" : "paused";

    return () => {
      delete document.documentElement.dataset.animationState;
    };
  }, [pageActive]);

  return (
    <div data-page-active={pageActive ? "true" : "false"} className="dental-page min-h-screen bg-[#fbfafc] font-sans text-[#17131b]">
      <Seo lang="en" document={seo} />
      <Nav lang="en" autoHideOnScroll />

      <main ref={mainRef}>
        <DentalHeroExecution
          downloadPath={heroDownloadPath}
          contactPath={contactPath}
          onCtaClick={() => reportLeadEntryConversion(location.search)}
        />

        <DentalFrontDeskStory />

        <DentalImpactGrowth />

        <DentalConversationDashboard />

        <DentalSystemIntegrations />
        <DentalPmsIntegrationVideo />

        <section
          id="workflow-console"
          data-dental-centered-anchor
          className="scroll-mt-28 bg-[#f5f1f7] px-5 py-12 sm:px-10 sm:py-16 lg:py-8"
        >
          <div className="dental-content">
            <div className="text-center">
              <h2 className="text-balance text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px] lg:whitespace-nowrap">
                See <span className="text-[#6d35cf]">ClawEase</span> book like a real receptionist.
              </h2>
            </div>
            <DentalWorkflowConsole />
          </div>
        </section>

        <Pricing lang="en" showIntro={false} />

        <DentalFaq />
      </main>
      <DentalPageNavigation mainRef={mainRef} />
    </div>
  );
}
